import { useState, useCallback, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import SessionService from "../../../../services/session_service";
import ActivityService from "../../../../services/activity_service";
import LevelService from "../../../../services/level_service";
import EnrollmentService from "../../../../services/enrollment_service";
import EvaluationService from "../../../../services/evaluation_service";
import EventService from "../../../../services/event_service";
import ClassPlanningService from "../../../../services/class_planning_service";
import { useTenant } from "../../../../hooks/useTenant";
import { addDays, toISODate, getStartOfWeek } from "../../../../utils/date";

/**
 * usePlanningData
 * Hook para a Grade de Planejamento.
 * Busca sessões com evaluationSystem=true e gerencia geração/regeneração do plano.
 */
export const usePlanningData = ({ referenceDate, view }) => {
    const { idTenant, loading: tenantLoading } = useTenant();
    const [sessions, setSessions] = useState([]);
    const [sessionsCache, setSessionsCache] = useState({});
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);

    // Progresso: { current, total }
    const [progress, setProgress] = useState({ current: 0, total: 0 });

    // Calcula range de datas
    const dateRange = useMemo(() => {
        if (view === "day") {
            const isoDate = toISODate(referenceDate);
            return { startDate: isoDate, endDate: isoDate };
        }
        const weekStart = getStartOfWeek(referenceDate);
        const weekEnd = addDays(weekStart, 6);
        return {
            startDate: toISODate(weekStart),
            endDate: toISODate(weekEnd),
        };
    }, [referenceDate, view]);

    // Fetch sessões com evaluationSystem=true
    const fetchSessions = useCallback(async (forceRefresh = false) => {
        if (!idTenant || tenantLoading) return;

        const cacheKey = `${dateRange.startDate}_${dateRange.endDate}_${view}`;
        if (!forceRefresh && sessionsCache[cacheKey]) {
            setSessions(sessionsCache[cacheKey]);
            return;
        }

        setLoading(true);
        try {
            const result = await SessionService.getSessions(
                idTenant,
                dateRange.startDate,
                dateRange.endDate
            );

            // Filtrar apenas sessões com evaluationSystem
            const filtered = result
                .filter(s => s.evaluationSystem === true)
                .map(s => ({ ...s, idSession: s.id }));

            setSessions(filtered);
            setSessionsCache(prev => ({ ...prev, [cacheKey]: filtered }));
        } catch (error) {
            console.error("[usePlanningData] Erro ao buscar sessões:", error);
            toast.error("Erro ao carregar grade de planejamento.");
            setSessions([]);
        } finally {
            setLoading(false);
        }
    }, [idTenant, tenantLoading, dateRange.startDate, dateRange.endDate, view, sessionsCache]);

    useEffect(() => {
        if (!tenantLoading && idTenant) {
            fetchSessions();
        }
    }, [tenantLoading, idTenant, fetchSessions]);

    // Agrupar sessões por classGroupId (ou idClass se sem grupo)
    const sessionGroups = useMemo(() => {
        const groups = {};
        sessions.forEach(s => {
            const groupKey = s.classGroupId || s.idClass || "unknown";
            if (!groups[groupKey]) {
                groups[groupKey] = {
                    groupId: groupKey,
                    idActivity: s.idActivity,
                    activityName: s.activityName,
                    classIds: new Set(),
                    sessions: [],
                    hasPlans: true,
                    needsRegeneration: false
                };
            }
            groups[groupKey].classIds.add(s.idClass);
            groups[groupKey].sessions.push(s);
            if (!s.lessonPlan) groups[groupKey].hasPlans = false;
        });

        // Converter Sets para Arrays
        return Object.values(groups).map(g => ({
            ...g,
            classIds: Array.from(g.classIds)
        }));
    }, [sessions]);

    /**
     * Gera planejamento para TODOS os grupos de uma vez.
     * Exibe apenas 1 toast no final e atualiza progresso.
     */
    const generateAllPlans = useCallback(async () => {
        if (!idTenant || generating || sessionGroups.length === 0) return;

        setGenerating(true);
        setProgress({ current: 0, total: sessionGroups.length });

        let totalSessions = 0;
        let errors = 0;

        try {
            // Buscar evento ativo mais recente (uma vez)
            let currentEventId = "";
            try {
                const events = await EventService.getAll(idTenant);
                const activeEvents = (events || [])
                    .filter(e => e.type !== "extra" && e.status !== "closed")
                    .sort((a, b) => {
                        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
                        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
                        return dateB - dateA;
                    });
                currentEventId = activeEvents[0]?.id || "";
            } catch {
                console.warn("[usePlanningData] Sem eventos disponíveis.");
            }

            // Processar cada grupo
            for (let i = 0; i < sessionGroups.length; i++) {
                const group = sessionGroups[i];
                setProgress({ current: i + 1, total: sessionGroups.length });

                try {
                    const result = await ClassPlanningService.generatePlanForGroup(
                        idTenant,
                        group.idActivity,
                        group.groupId,
                        group.classIds,
                        currentEventId,
                        { ActivityService, LevelService, EnrollmentService, EvaluationService }
                    );
                    totalSessions += result.sessionsUpdated;
                } catch (error) {
                    console.error(`[usePlanningData] Erro no grupo ${group.groupId}:`, error);
                    errors++;
                }
            }

            // 1 toast final
            if (errors === 0) {
                toast.success(`Planejamento gerado com sucesso! ${totalSessions} sessão(ões) atualizada(s).`);
            } else {
                toast.warning(`Planejamento parcial: ${totalSessions} sessões atualizadas, ${errors} grupo(s) com erro.`);
            }

            // Recarregar dados forçando atualização do cache
            await fetchSessions(true);
        } catch (error) {
            console.error("[usePlanningData] Erro geral ao gerar planos:", error);
            toast.error("Erro ao gerar planejamento.");
        } finally {
            setGenerating(false);
            setProgress({ current: 0, total: 0 });
        }
    }, [idTenant, generating, sessionGroups, fetchSessions]);

    return {
        sessions,
        sessionGroups,
        loading,
        generating,
        progress,
        generateAllPlans,
        refresh: fetchSessions,
    };
};
